import { LightningElement, registerDecorators as _registerDecorators, registerDecorators as _registerDecorators2 } from 'lwc';
export function getNumberAttr(value, nullValue = 0) {
  if (value) {
    if (typeof value === 'string') {
      return Number.parseInt(value, 10);
    }
    return value;
  }
  return nullValue;
}
export function getObjectAttr(value, nullValue = null) {
  if (value) {
    if (typeof value == 'string') {
      try {
        return JSON.parse(value);
      } catch (error) {
        throw new Error(`Failed to initialize LWC component, the prop attribute '${value}' is not valid JSON.`, {
          cause: error
        });
      }
    }
    return value;
  }
  return nullValue;
}
export function getBooleanAttr(value, nullValue = false) {
  if (value) {
    if (typeof value == 'string') {
      return value.toLocaleLowerCase() === 'true';
    }
    return value;
  }
  return nullValue;
}
export function isEmpty(value) {
  if (typeof value === 'undefined') {
    return true;
  } else if (typeof value === 'string') {
    return value.trim() === '';
  } else if (value instanceof Set) {
    return value.size <= 0;
  }
  throw new Error(`Unexpected type '${typeof value}' sent to isEmpty function, only strings and Sets are supported`);
}
export class BaseInput extends LightningElement {
  constructor(...args) {
    super(...args);
    this.name = 'input';
    this.label = void 0;
    this.ariaLabel = void 0;
    this._value = void 0;
    this._required = false;
    this._errorMessage = '';
    this.isError = false;
    this.requiredMessage = 'This field is mandatory';
    this._forceShowError = void 0;
  }
  set forceShowError(value) {
    this._forceShowError = value;
    this.evaluateRequired();
  }
  get forceShowError() {
    return this._forceShowError;
  }
  get value() {
    return this._value;
  }
  set value(val) {
    if (val) {
      this.cleanError();
    } else {
      if (this._required) {
        this.setError(this.requiredMessage);
      }
    }
    this._value = val;
  }
  setError(message) {
    this._errorMessage = message;
    this.isError = true;
  }
  cleanError() {
    this._errorMessage = '';
    this.isError = false;
  }
  set required(value) {
    this._required = getBooleanAttr(value);
  }
  get required() {
    return this._required;
  }
  get stateClassName() {
    return this.isError ? 'error' : '';
  }
  handleFocus() {
    this.cleanError();
  }
  handleBlur() {
    // Re-Dispacth Event
    this.dispatchEvent(new CustomEvent('blur', {
      bubbles: true,
      composed: true,
      cancelable: true
    }));
    if (this.required && isEmpty(this._value)) {
      this.setError(this.requiredMessage);
    }
  }
  evaluateRequired() {
    if (this._forceShowError && this._required && isEmpty(this._value)) {
      this.setError(this.requiredMessage);
    }
  }
  connectedCallback() {
    this.evaluateRequired();
  }
  /*LWC compiler v7.1.3*/
}
BaseInput.delegatesFocus = true;
_registerDecorators(BaseInput, {
  publicProps: {
    name: {
      config: 0
    },
    label: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    requiredMessage: {
      config: 0
    },
    forceShowError: {
      config: 3
    },
    value: {
      config: 3
    },
    required: {
      config: 3
    }
  },
  publicMethods: ["setError", "cleanError"],
  track: {
    _value: 1
  },
  fields: ["_required", "_errorMessage", "isError", "_forceShowError"]
});
export class OptionsInput extends BaseInput {
  constructor(...args) {
    super(...args);
    this._options = void 0;
    this._multiple = false;
  }
  get multiple() {
    return this._multiple;
  }
  set multiple(value) {
    this._multiple = getBooleanAttr(value);
  }
  set options(value) {
    if (typeof value === 'string') {
      try {
        this._options = JSON.parse(value);
      } catch (error) {
        if (error instanceof SyntaxError) {
          this._options = [];
          value.split(',').forEach(word => {
            const trimmed = word.trim();
            this._options.push({
              value: trimmed,
              label: trimmed
            });
          });
        } else {
          throw error;
        }
      }
    } else {
      this._options = value;
    }
  }
  get options() {
    return this._options;
  }
  get transformedOptions() {
    const {
      options,
      value
    } = this;
    if (Array.isArray(options)) {
      const retValue = options.map((option, index) => ({
        ...option,
        key: `index-${index}`,
        checked: OptionsInput.checked(value, option)
      }));
      return retValue;
    }
    return [];
  }
  static checked(value, option) {
    return typeof value === 'string' && value === (option.value || option.label) || Array.isArray(value) && value.includes(option.value);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators2(OptionsInput, {
  publicProps: {
    multiple: {
      config: 3
    },
    options: {
      config: 3
    }
  },
  fields: ["_options", "_multiple"]
});